<template>
    <div class="foal-marking-data">
        <h2 class="section-header marking-data-header">{{$t('message.horseCreate.importedHorseMarkingDataHeader')}}</h2>
        <div class="flex gap-5 data-and-img" v-if="!onlyMarking">
            <div class="w-8 flex flex-column gap-3 inputs-box">
                <CustomCalendar name="controlDate" :label="$t('message.horseCreate.controlDate')"
                                v-model="horse.dateOfControl" required :customErrorMessage="getRequiredMessage(horse.dateOfControl)"
                                :disabled="disableEdit"/>
                <CustomAutocompleteVeterinarian name="controlledBy" :label="$t('message.horseCreate.controlledBy')"
                                                noPlaceholder v-model="horse.controlledBy" search-param="fullName"
                                                :disabled="disableEdit"/>
                <CustomInputText name="microchip" :label="$t('message.horseCreate.microchip')" noPlaceholder
                                 v-model="horse.transponderCode" required :customErrorMessage="getRequiredMessage(horse.transponderCode)"
                                 :max-length="15" :rules="() => (!horse.transponderCode || horse.transponderCode.length === 15) ? true : $t('message.validations.exactly_15_characters')"
                                 :disabled="disableEdit"/>
                <CustomInputText name="secondMicrochip" :label="$t('message.horseCreate.secondMicrochip')" noPlaceholder
                                 v-model="horse.secondTransponderCode"
                                 :disabled="disableEdit"/>
            </div>
            <div class="w-4 flex flex-column justify-content-center align-items-center gap-3 img-box">
                <div class="horse-photo-container">
                    <img :src="horse.photoUrl ? horse.photoUrl : getHorsePhoto()"
                        :alt="$t('message.horsePreview.horsePhoto')" class="horse-photo">
                </div>
                <input type="file" ref="file" v-show="false" @change="handleFile"
                    id="importFile" name="file" accept="image/*"/>
                <div v-if="horse.photoUrl"><CustomTextButton :label="$t('message.horseCreate.deletePhoto')" :disabled="disableEdit"/></div>
                <div v-else>
                    <CustomTextButton :label="$t('message.horseCreate.addPhoto')" @click="$refs.file.click()" :disabled="disableEdit"/>
                </div>
            </div>
        </div>
        <div>
            <div class="flex flex-column gap-3">
                <h3 class="subsection-header subsection-header__first">{{ $t('message.horseCreate.head') }}:</h3>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="starSize" :label="$t('message.horseCreate.starSize')" noPlaceholder v-model="horse.starSize" v-model:otherValue="horse.starSizeOther" :disabled="disableEdit" :items="starSizes"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="starPosition" :label="$t('message.horseCreate.starPosition')" noPlaceholder v-model="horse.starPosition" v-model:otherValue="horse.starPositionOther" :disabled="disableEdit" :items="starPositions"/>
                    </div>
                </div>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="starRelToWhorl" :label="$t('message.horseCreate.starRelToWhorl')" noPlaceholder v-model="horse.starRelToWhorl" v-model:otherValue="horse.starRelToWhorlOther" :disabled="disableEdit" :items="starRelationToWhorl"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="faceWhorl" :label="$t('message.horseCreate.faceWhorl')" noPlaceholder v-model="horse.faceWhorl" v-model:otherValue="horse.faceWhorlOther" :disabled="disableEdit" :items="faceWhorls"/>
                    </div>
                </div>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="stripe" :label="$t('message.horseCreate.stripe')" noPlaceholder v-model="horse.stripe" v-model:otherValue="horse.stripeOther" :disabled="disableEdit" :items="stripes"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="snipAndFleshMark" :label="$t('message.horseCreate.snipAndFleshMark')" noPlaceholder v-model="horse.snipAndFleshMark" v-model:otherValue="horse.snipAndFleshMarkOther" :disabled="disableEdit" :items="snipAndFleshMarks"/>
                    </div>
                </div>
            </div>
            <div class="flex flex-column gap-3">
                <h3 class="subsection-header subsection-header__first">{{ $t('message.horseCreate.limbs') }}:</h3>
                <h4 class="subsection-header subsection-header__second">{{ $t('message.horseCreate.forelegL') }}:</h4>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="whiteMarksLF" :label="$t('message.horseCreate.whiteMarks')" noPlaceholder v-model="horse.whiteMarksLF" v-model:otherValue="horse.whiteMarksLFOther" :disabled="disableEdit" :items="whiteMarks"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="spotOnCoronetLF" :label="$t('message.horseCreate.spotOnCoronet')" noPlaceholder v-model="horse.spotOnCoronetLF" v-model:otherValue="horse.spotOnCoronetLFOther" :disabled="disableEdit" :items="spotOnCoronet"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="hoofLF" :label="$t('message.horseCreate.hoof')" noPlaceholder v-model="horse.hoofLF" v-model:otherValue="horse.hoofLFOther" :disabled="disableEdit" :items="hoofTypes"/>
                    </div>
                </div>
                <h4 class="subsection-header subsection-header__second">{{ $t('message.horseCreate.forelegR') }}:</h4>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="whiteMarksRF" :label="$t('message.horseCreate.whiteMarks')" noPlaceholder v-model="horse.whiteMarksRF" v-model:otherValue="horse.whiteMarksRFOther" :disabled="disableEdit" :items="whiteMarks"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="spotOnCoronetRF" :label="$t('message.horseCreate.spotOnCoronet')" noPlaceholder v-model="horse.spotOnCoronetRF" v-model:otherValue="horse.spotOnCoronetRFOther" :disabled="disableEdit" :items="spotOnCoronet"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="hoofRF" :label="$t('message.horseCreate.hoof')" noPlaceholder v-model="horse.hoofRF" v-model:otherValue="horse.hoofRFOther" :disabled="disableEdit" :items="hoofTypes"/>
                    </div>
                </div>
                <h4 class="subsection-header subsection-header__second">{{ $t('message.horseCreate.hindLegL') }}:</h4>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="whiteMarksLH" :label="$t('message.horseCreate.whiteMarks')" noPlaceholder v-model="horse.whiteMarksLH" v-model:otherValue="horse.whiteMarksLHOther" :disabled="disableEdit" :items="whiteMarks"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="spotOnCoronetLH" :label="$t('message.horseCreate.spotOnCoronet')" noPlaceholder v-model="horse.spotOnCoronetLH" v-model:otherValue="horse.spotOnCoronetLHOther" :disabled="disableEdit" :items="spotOnCoronet"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="hoofLH" :label="$t('message.horseCreate.hoof')" noPlaceholder v-model="horse.hoofLH" v-model:otherValue="horse.hoofLHOther" :disabled="disableEdit" :items="hoofTypes"/>
                    </div>
                </div>
                <h4 class="subsection-header subsection-header__second">{{ $t('message.horseCreate.hindLegR') }}:</h4>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="whiteMarksRH" :label="$t('message.horseCreate.whiteMarks')" noPlaceholder v-model="horse.whiteMarksRH" v-model:otherValue="horse.whiteMarksRHOther" :disabled="disableEdit" :items="whiteMarks"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="spotOnCoronetRH" :label="$t('message.horseCreate.spotOnCoronet')" noPlaceholder v-model="horse.spotOnCoronetRH" v-model:otherValue="horse.spotOnCoronetRHOther" :disabled="disableEdit" :items="spotOnCoronet"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="hoofRH" :label="$t('message.horseCreate.hoof')" noPlaceholder v-model="horse.hoofRH" v-model:otherValue="horse.hoofRHOther" :disabled="disableEdit" :items="hoofTypes"/>
                    </div>
                </div>
            </div>
            <div class="flex flex-column gap-3">
                <h3 class="subsection-header subsection-header__first">{{ $t('message.horseCreate.body') }}:</h3>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="underMainWhorlsLeft" :label="$t('message.horseCreate.underMainWhorlLeft')" noPlaceholder v-model="horse.underMainWhorlsLeft" v-model:otherValue="horse.underMainWhorlsLeftOther" :disabled="disableEdit" :items="underMainWhorls"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="underMainWhorlsRight" :label="$t('message.horseCreate.underMainWhorlRight')" noPlaceholder v-model="horse.underMainWhorlsRight" v-model:otherValue="horse.underMainWhorlsRightOther" :disabled="disableEdit" :items="underMainWhorls"/>
                    </div>
                </div>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="underNeckWhorls" :label="$t('message.horseCreate.underNeckWhorl')" noPlaceholder v-model="horse.underNeckWhorls" v-model:otherValue="horse.underNeckWhorlsOther" :disabled="disableEdit" :items="underNeckWhorls"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <!-- Empty div to maintain layout -->
                    </div>
                </div>
            </div>
            <div class="flex flex-column gap-3">
                <h3 class="subsection-header subsection-header__first">{{ $t('message.horseCreate.marking') }}:</h3>
                <div class="flex gap-3 flex-wrap">
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="marks" :label="$t('message.horseCreate.marks')" noPlaceholder v-model="horse.marks" v-model:otherValue="horse.marksOther" :disabled="disableEdit" :items="markTypes"/>
                    </div>
                    <div class="flex-1 min-w-0">
                        <CustomMultiSelectMarking name="position" :label="$t('message.horseCreate.position')" noPlaceholder v-model="horse.markPositions" v-model:otherValue="horse.markPositionsOther" :disabled="disableEdit" :items="markPositions"/>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <h3>{{$t('message.horseCreate.dnaCard')}}
                <a v-if="horse.horseDnaDocumentUrl" :href="horseDnaDocumentUrl" download target="_blank"
                   v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"  style="color: #192134">
                    <i class="pi pi-cloud-download download-button"/>
                </a>
            </h3>
        </div>
        <div v-if="!onlyMarking" v-for="(test, i) in horse.laboratoryTest" :key="i" class="flex flex-column gap-3 mt-5 dna-section">
            <h2 class="section-header dna-header">{{$t('message.horseCreate.importedHorseDNASample')}} #{{ i + 1 }}</h2>
            <div v-if="loaded" class="pr-2 w-8 flex flex-column gap-3 test-box section-details-inner">
                <CustomSelectOne v-if="test.laboratoryTestStatus !== 'NOT_STARTED'" name="testResult" :label="$t('message.laboratoryHorses.form.testRes')"
                                :required="computedRequired" noPlaceholder :items="testResults" v-model="test.laboratoryTestResult" :disabled="true"
                                itemValue="value"/>
                <CustomSelectOne v-if="test.laboratoryTestResult" name="why" :label="$t('message.laboratoryHorses.form.why')" :required="computedRequired"
                                    noPlaceholder :items="computedStatuses.length > 0 ? computedStatuses : testStatuses" :key="testResult"
                                    itemValue="value" :disabled="true"
                                    v-model="test.laboratoryTestStatus" />
                <CustomSelectOne name="laboratory" :items="laboratories" noPlaceholder
                    :label="$t('message.horseCreate.laboratory')" v-model="test.laboratoryId"
                    :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'"
                    :required="computedRequired" itemValue="id" itemLabel="name"/>
            </div>
            <div class="flex gap-6 checkbox-container">
                <span>{{ $t('message.horseCreate.samplesFrom') }}:</span>
                <div class="flex gap-7 checkboxes">
                    <CustomCheckbox name="hair" :label="$t('message.horseCreate.hair')"
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED' || disableEdit"
                        v-model="test.dnaHair"/>
                    <CustomCheckbox name="blood" :label="$t('message.horseCreate.blood')"
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED' || disableEdit"
                        v-model="test.dnaBlood"/>
                    <CustomCheckbox name="bone" :label="$t('message.horseCreate.bone') || disableEdit"
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'"
                        v-model="test.dnaBone"/>
                    <CustomCheckbox name="ftaCard" :label="$t('message.horseCreate.ftaCard') || disableEdit"
                                    :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'"
                                    v-model="test.dnaFtaCard"/>
                </div>
            </div>
            <div class="flex gap-3 inputs-container">
                <div class="flex flex-column gap-3 w-6 inputs-subcontainer">
                    <CustomInputText name="batchRefNum" :label="$t('message.horseCreate.batchRefNum')" noPlaceholder
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'" v-model="test.batchRefNumber"/>
                    <CustomCalendar name="shipment" :label="$t('message.horseCreate.shipmentDate')"
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'" v-model="test.shipmentDate"/>
                </div>
                <div class="flex flex-column gap-3 w-6 inputs-subcontainer">
                    <CustomSelectOneTestType name="testReq" :label="$t('message.horseCreate.testReq')" noPlaceholder
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'" v-model="test.testType"/>
                    <CustomSelectOneTestPriority name="testPriority" :label="$t('message.horseCreate.testPrior')" noPlaceholder
                        :disabled="test.laboratoryTestStatus !== 'NOT_STARTED'" v-model="test.testPriority"/>
                </div>
            </div>
        </div>
        <div class="flex gap-3 mt-5">
            <div class="w-6">
                <h3>{{$t('message.horseCreate.sireDnaCard')}}
                    <a v-if="horse.sireDnaDocumentUrl" :href="sireDnaDocumentUrl" download target="_blank"
                       v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"  style="color: #192134">
                        <i class="pi pi-cloud-download download-button"/>
                    </a>
                </h3>
                <CustomDnaCardUploader :url="sireUploadUrl" name="sireDnaDocument" :disabled="disableEdit"/>
            </div>
            <div class="w-6">
            <h3>{{$t('message.horseCreate.damDnaCard')}}
                    <a v-if="horse.damDnaDocumentUrl" :href="damDnaDocumentUrl" download target="_blank"
                       v-tooltip.top="$t('message.horseCreate.downloadDnaCard')" style="color: #192134">
                        <i class="pi pi-cloud-download download-button"/>
                    </a>
                </h3>

                <CustomDnaCardUploader :url="damUploadUrl" name="damDnaDocument" :disabled="disableEdit"/>
            </div>
        </div>
    </div>
</template>

<script>
    import CustomCalendar from '@/components/form/CustomCalendar.vue';
    import CustomCheckbox from '@/components/form/CustomCheckbox.vue';
    import CustomInputText from '@/components/form/CustomInputText.vue';
    import CustomTextButton from '@/components/form/CustomTextButton.vue';
    import CustomSelectOne from '@/components/form/inner/CustomSelectOne.vue';
    import {
        getTestResults,
        getTestStatuses,
        getAllLaboratories,
        getStarSizes,
        getStarPositions,
        getStarRelationToWhorl,
        getFaceWhorls,
        getStripes,
        getSnipAndFleshMarks,
        getWhiteMarks,
        getSpotOnCoronet,
        getHoofTypes,
        getUnderMainWhorl,
        getUnderNeckWhorls,
        getMarkTypes,
        getMarkPositions,
    } from "@/swagger/vue-api-client";
    import {SystemRole} from "@/utils/SystemRole";
    import {HorseStatus} from "@/utils/HorseStatus";
    import CustomSelectOneTestPriority from '@/components/form/CustomSelectOneTestPriority.vue';
    import CustomSelectOneTestType from '@/components/form/CustomSelectOneTestType.vue';
    import CustomAutocompleteVeterinarian from "@/components/form/CustomAutocompleteVeterinarian.vue";
    import FileUpload from "primevue/fileupload";
    import CustomDnaCardUploader from "@/components/form/CustomDnaCardUploader.vue";
    import Tooltip from "primevue/tooltip";
    import CustomMultiSelectMarking from "@/components/form/CustomMultiSelectMarking.vue";

    export default {
        name: "FoalMarkingData",

        components: {
            CustomMultiSelectMarking,
            CustomDnaCardUploader,
            CustomAutocompleteVeterinarian,
            CustomCalendar,
            CustomInputText,
            CustomCheckbox,
            CustomSelectOne,
            CustomTextButton,
            CustomSelectOneTestPriority,
            CustomSelectOneTestType,
            FileUpload
        },

        props: {
            modelValue: {
                type: Object,
                required: true,
            },
            showErrorMessages: {
                type: Boolean,
                default: false,
            },
            systemRole: {
                type: String,
                required: true,
            },
            onlyMarking: {
                type: Boolean,
                default: false,
            },
            disableEdit: {
                type: Boolean,
                default: false,
            },
            addFoalView: {
                type: Boolean,
                default: false,
            }
        },

        emits: ["update:modelValue"],

        data() {
            return {
                loaded: false,
                testResults: [],
                testResult: null,
                computedStatuses: [],
                importedPhoto: null,
                laboratories: [],
                damDnaDocumentUrl: this.modelValue.damDnaDocumentUrl,
                sireDnaDocumentUrl: this.modelValue.sireDnaDocumentUrl,
                horseDnaDocumentUrl: this.modelValue.horseDnaDocumentUrl,
                // Marking data
                starSizes: [],
                starPositions: [],
                starRelationToWhorl: [],
                faceWhorls: [],
                stripes: [],
                snipAndFleshMarks: [],
                whiteMarks: [],
                spotOnCoronet: [],
                hoofTypes: [],
                underMainWhorls: [],
                underNeckWhorls: [],
                markTypes: [],
                markPositions: [],
            };
        },

        directives: {
            tooltip: Tooltip,
        },

        methods: {
            getHorsePhoto() {
                if (document.body.classList.contains("arab-horses")) return require("../../../../assets/horse-graphic.png");
                return require("../../../../assets/Preview-Image-thoroughbred.jpg")
            },
            handleCheckboxChange(item, option, value) {
                if (value) {
                    item.dnaSampleType = option;
                } else {
                    item.dnaSampleType = null;
                }
            },
            getRequiredMessage(value) {
                if (this.showErrorMessages && (value === null || value === "")) {
                    return this.$t("message.validations.required");
                }
                return "";
            },
            handleFile() {
                const file = this.$refs.file.files[0];

                if (!file) return;

                const reader = new FileReader();

                reader.onload = function (e) {
                    const img = document.querySelector(".horse-photo");
                    img.src = e.target.result;
                };

                reader.readAsDataURL(file);

                const formData = new FormData();

                formData.append("files", file);

                this.importedPhoto = formData;

                this.$emit("importPhoto", formData);
            },
        },

        beforeMount() {
            getTestResults()
                .then((res) => {
                    this.testResults = res.data;
                    return getTestStatuses()
                        .then((res) => {
                            this.testStatuses = res.data;
                            this.loaded = true;
                        });
                });
            getAllLaboratories()
                .then((res) => {
                    this.laboratories = res.data;
                });

            // Load marking data
            getStarSizes().then((res) => {
                this.starSizes = res.data;
            });
            getStarPositions().then((res) => {
                this.starPositions = res.data;
            });
            getStarRelationToWhorl().then((res) => {
                this.starRelationToWhorl = res.data;
            });
            getFaceWhorls().then((res) => {
                this.faceWhorls = res.data;
            });
            getStripes().then((res) => {
                this.stripes = res.data;
            });
            getSnipAndFleshMarks().then((res) => {
                this.snipAndFleshMarks = res.data;
            });
            getWhiteMarks().then((res) => {
                this.whiteMarks = res.data;
            });
            getSpotOnCoronet().then((res) => {
                this.spotOnCoronet = res.data;
            });
            getHoofTypes().then((res) => {
                this.hoofTypes = res.data;
            });
            getUnderMainWhorl().then((res) => {
                this.underMainWhorls = res.data;
            });
            getUnderNeckWhorls().then((res) => {
                this.underNeckWhorls = res.data;
            });
            getMarkTypes().then((res) => {
                this.markTypes = res.data;
            });
            getMarkPositions().then((res) => {
                this.markPositions = res.data;
            });
        },

        mounted() {
            if (this.addFoalView && this.horse.horseStatus !== HorseStatus.LABORATORY_DATA_ENTERED) {
                let checkForEmpty = false;
                if (this.horse.laboratoryTest.length > 0) {
                    this.horse.laboratoryTest.forEach((test) => {
                        if (test.laboratoryTestStatus === "NOT_STARTED") {
                            checkForEmpty = true;
                        }
                    });
                }
                if (!checkForEmpty) {
                    this.horse.laboratoryTest.push({
                        id: null,
                        laboratoryTestStatus: "NOT_STARTED",
                        selectedLaboratoryTestStatus: null,
                        testType: null,
                        testPriority: null,
                        dnaHair: false,
                        dnaBlood: false,
                        dnaBone: false,
                        dnaFtaCard: false,
                        batchRefNumber: null,
                        shipmentDate: null,
                        shipmentReceivedDate: null,
                        dnaReferenceNumber: null,
                        notes: null,
                    })
                }
            }
        },

        computed: {
            horse: {
                get() { return this.modelValue; },
                set(value) { this.$emit("update:modelValue", value); },
            },
            computedRequired() {
                const allowedRoles = [
                    SystemRole.SECOND_LEVEL_EDITOR,
                    SystemRole.THIRD_LEVEL_EDITOR,
                    SystemRole.ADMIN,
                ];

                return allowedRoles.includes(this.systemRole)
            },
            sireUploadUrl() {
                return `${process.env.VUE_APP_API_URL}/files/dna-card/` + this.horse.sireId
            },
            damUploadUrl() {
                return `${process.env.VUE_APP_API_URL}/files/dna-card/` + this.horse.damId
            }

        },

        watch: {
            testResult: {
                handler(testRes) {
                    this.computedStatuses = [];
                    if (testRes) {
                        this.testStatuses.forEach((tS) => {
                            if (testRes.value === tS.extendedValue) {
                                this.computedStatuses.push(tS);
                            }
                        });
                    }
                },
                deep: true,
            }
        },
    };
</script>

<style lang="scss">
    @import "../../../../assets/theme/mytheme/variables";

    .foal-marking-data {
        .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .custom-autocomplete {
            padding-left: 170px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 160px;
                text-align: right;
            }
        }
        .checkbox-container {
            padding-left: 45px;
        }
        .horse-photo-container {
            position: relative;
            .horse-photo {
                height: auto;
                width: 100%;
                background: var(--primary-color);
                border-radius: 12px;
            }
        }
        .section-header {
            font-size: 30px;
            &.dna-header, &.marking-data-header {
                font-size: 25px;
                color: var(--header-color);
                font-family: "EB Garamond", serif;
                font-weight: 500;
            }
            &.marking-data-header {
                font-size: 30px;
                font-weight: normal;
            }
        }
        h3 {
            margin-top: 3rem;
            margin-bottom: 0;
            &.subsection-header__first {
                padding-left: 98px;
            }
            &.subsection-header__second {
                padding-left: 20px;
            }
            &.subsection-header__third {
                padding-left: 75px;
            }
        }
        .dna-section {
            background-color: var(--secondary-color);
            padding: 1.5rem;
            border-radius: var(--border-radius);
        }
        @media screen and (max-width: 900px) {
            .data-and-img {
                flex-direction: column;
                .inputs-box {
                    width: 100% !important;
                }
                .img-box {
                    align-self: center;
                }
            }
            .dna-section {
                .test-box {
                    width: 100% !important;
                }
                .checkbox-container {
                    gap: 1rem !important;
                    flex-direction: column;
                }
                .inputs-container {
                    flex-direction: column;
                    .inputs-subcontainer {
                        width: 100% !important;
                    }
                }
            }
        }
        @media screen and (max-width: 600px) {
            .dna-section {
                .checkbox-container {
                    .checkboxes {
                        flex-wrap: wrap;
                        align-items: center;
                        justify-content: center;
                        gap: 1rem !important;
                    }
                }
            }
        }
    }

    a {
        > i.download-button {
            font-size: 20px;
        }
    }

    body.arab-horses {
        .foal-marking-data {
            .horse-photo-container {
                position: relative;
                .horse-photo {
                    height: 350px;
                    width: unset;
                    background: var(--primary-color);
                    border-radius: 12px;
                }
            }
        }
    }
</style>
